import { readStamped, writeStamped, clearStampedForOwner } from './activeRoleStorage';

/**
 * Has this user hidden the "needs you" strip for this workspace (issue 713)?
 *
 * Client state, not an answer from the server: the strip's CONTENT is whatever
 * user-api says is open and fixable by this person, "Ausblenden" only decides
 * whether this login wants to see it. Stamped to the user (never the e-mail) and
 * keyed per workspace slug, exactly like the demo bar's intro line, so a second
 * person on the same browser never inherits it.
 *
 * It ends when the next login STARTS, not when the last one ends: a fresh credential
 * login (password, passkey, SSO return, invite acceptance) calls
 * `clearNeedsYouStripHiddenOnSignIn`, because a login ends in more ways than a
 * sign-out button — idle timeout, an expired session, a closed browser — and only
 * the deliberate one reaches `clearDraftsOnSignOut` (which clears it too, harmlessly).
 * Arriving in the workspace through an SSO ticket counts as a sign-in, whichever
 * way the ticket was minted: that includes the landing checkout's handoff of a
 * session already running on the landing host. A token refresh and a module app
 * receiving the workspace's token (`setToken`) are not sign-ins and never touch it.
 */
const HIDDEN_VALUE_PREFIX = 'needsYouStripHidden:';
const HIDDEN_OWNER_PREFIX = 'needsYouStripHiddenOwner:';

function hiddenKeys(tenantSlug: string | null | undefined): { valueKey: string; ownerKey: string } | null {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return null;
  return { valueKey: `${HIDDEN_VALUE_PREFIX}${slug}`, ownerKey: `${HIDDEN_OWNER_PREFIX}${slug}` };
}

export function needsYouStripHidden(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  const keys = hiddenKeys(tenantSlug);
  if (!userId || !keys) return false;
  return readStamped(keys.valueKey, keys.ownerKey, userId) === '1';
}

/** Remember the choice. Without a workspace or an owner to stamp it with there is nothing to persist. */
export function hideNeedsYouStrip(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = hiddenKeys(tenantSlug);
  if (!userId || !keys) return;
  writeStamped(keys.valueKey, keys.ownerKey, userId, '1');
}

/** Forget one account's choice only — a colleague's stays. */
export function clearNeedsYouStripHiddenForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  const keys = hiddenKeys(tenantSlug);
  if (!keys) return;
  clearStampedForOwner(keys.valueKey, keys.ownerKey, userId);
}

/**
 * A fresh login just completed for `userId`: forget that account's choice in EVERY
 * workspace on this browser (the slug is unknown to the login screen, and the
 * choice is per login, not per workspace). Entries stamped to anyone else stay.
 */
export function clearNeedsYouStripHiddenOnSignIn(userId: string | null | undefined): void {
  if (!userId) return;
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return;
  }
  for (const key of keys) {
    if (!key.startsWith(HIDDEN_VALUE_PREFIX)) continue;
    const slug = key.slice(HIDDEN_VALUE_PREFIX.length);
    clearNeedsYouStripHiddenForOwner(slug, userId);
  }
}
